import { Option, Result } from '@sapphire/result';

import {
  Qualification,
  StandardSurveyPayloadInput,
  SurveyPricingSnapshot,
} from '@ps/survey-utils';

import { SurveyAudienceForm } from '../../../utils';
import { CachedEntry } from '../../../utils/cache/cache.helper';
import { InsightsChildState } from '../../insights/survey-creation-insights-state.model';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import { SurveyChildEntry } from '../../survey-creation-child-registry.helper';
import { childPricing } from '../domain/child-pricing';

export interface PreDraftChildDraft {
  readonly key: SurveyChildKey;
  readonly input: StandardSurveyPayloadInput;
}

export interface PreDraftChildDraftsInput {
  /** Keyed entries — the child state no longer carries its own key. */
  readonly children: readonly SurveyChildEntry[];
  /** Shared across rows: offer price, pause threshold and blend come from the one form. */
  readonly pricing: SurveyPricingSnapshot;
  /** Feasibility is estimated per row, so each row carries its own CPI. */
  readonly insightsChildren: Record<SurveyChildKey, InsightsChildState>;
  readonly qualificationsRecord: Record<string, CachedEntry<Qualification[]>>;
  readonly userId: number;
  readonly companyId: number;
  /** Reported when the buyer has ticked fewer rows than the flow allows. */
  readonly tooFewSelectedMessage: string;
  /** Rows a flow needs before it can be saved — 2 for a tracker, 1 otherwise. */
  readonly minimumSelected?: number;
  /** Reported when a ticked row is invalid but carries no error of its own. */
  readonly invalidChildMessage: string;
}

export function buildPreDraftChildDrafts(
  input: PreDraftChildDraftsInput,
): Result<PreDraftChildDraft[], string> {
  const selected = input.children.filter(
    ([, , { participant }]) => participant,
  );

  if (selected.length < (input.minimumSelected ?? 1)) {
    return Result.err(input.tooFewSelectedMessage);
  }

  const drafts: PreDraftChildDraft[] = [];

  for (const entry of selected) {
    const draft = preDraftChildDraft(entry, input);

    if (draft.isErr()) {
      return Result.err(draft.unwrapErr());
    }

    drafts.push(draft.unwrap());
  }

  return Result.ok(drafts);
}

export function preDraftChildDraft(
  [key, child]: SurveyChildEntry,
  {
    pricing,
    insightsChildren,
    qualificationsRecord,
    userId,
    companyId,
    invalidChildMessage,
  }: PreDraftChildDraftsInput,
): Result<PreDraftChildDraft, string> {
  const form = SurveyAudienceForm.from({
    metadata: child.metadata,
    qualifications: child.qualifications,
    quotas: child.quotas,
  });

  return Option.from(form.data).match({
    some: ({ metadata, qualifications, quotas }) =>
      Result.ok<PreDraftChildDraft>({
        key,
        input: {
          metadata,
          pricing: childPricing(pricing, insightsChildren[key]),
          userId,
          companyId,
          targeting: {
            qualifications,
            quotas,
            catalogQualifications:
              qualificationsRecord[String(key)]?.data ?? [],
          },
          enableRespondentStatusFilter: false,
        } satisfies StandardSurveyPayloadInput,
      }),
    none: () =>
      Result.err(Option.from(form.error).unwrapOr(invalidChildMessage)),
  });
}
